import { trackingEvents } from '../services/tracking/trackConfig';
import { EventInputData } from '../types/tracking';
import { CollectionProperties } from '../types/AppContent';
import { ItemVariation } from '@klab-berlin/api-sdk/lib/types/responses/Collection';
import { AmplitudeTrackPayload } from '@klab-berlin/api-sdk/lib/types/requests/Track';
import { Course, MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { amplitudeContentTypes, amplitudeEvents, ContentViewedPageSource } from '../constants';
import { getContentEventPayload, waitForAmpliReady } from './ampli';
import services from '../services';

type FromType = 'menu' | 'context';

export const createPinTrackingConfig = (
  isPinned: boolean,
  itemType: ItemVariation,
  from: FromType,
  payload: any
) => {
  const eventPayload: EventInputData = {};
  const events = {
    onClick: {},
  };

  if (itemType === 'mindItem') {
    eventPayload.document = payload;
    if (from === 'context') {
      events.onClick = isPinned
        ? trackingEvents.mindItemContextUnpin
        : trackingEvents.mindItemContextPin;
    }

    if (from === 'menu') {
      events.onClick = isPinned
        ? trackingEvents.mindItemMenuUnpin
        : trackingEvents.mindItemMenuPin;
    }
  }

  if (itemType === 'product') {
    eventPayload.product = payload;

    if (from === 'context') {
      events.onClick = isPinned
        ? trackingEvents.productContextUnpin
        : trackingEvents.productContextPin;
    }

    if (from === 'menu') {
      events.onClick = isPinned
        ? trackingEvents.productMenuUnpin
        : trackingEvents.productMenuPin;
    }
  }

  return {
    eventPayload,
    events,
  };
};

export const createFollowCuratedCollectionTrackingConfig = (
  isFollowed: boolean,
  collection: CollectionProperties
) => {
  const events = {
    onClick: isFollowed
      ? trackingEvents.curatedCollectionUnfollow
      : trackingEvents.curatedCollectionFollow,
  };

  const eventPayload: EventInputData = { collection };

  return {
    eventPayload,
    events,
  };
};

export type NavItemType =
  | 'start'
  | 'search'
  | 'pinnedDocuments'
  | 'premium'
  | 'menu'
  | 'profile'
  | 'schoolOffer'
  | 'contact'
  | 'logout'
  | 'faq'
  | 'imprint'
  | 'agb'
  | 'privacyPolicy'
  | 'intercom'
  | 'collections'
  | 'downloads'
  | 'followedCollections'
  | 'publicCollections';

export type CarouselTrackingPropertiesGTM = {
  carousel_school_types?: string[];
  carousel_subject_texts?: string[];
  carousel_document_ids: string[];
  carousel_type:
    | 'placeholder'
    | 'carousel'
    | 'pinnedDocuments'
    | 'recommendedProducts'
    | 'recommendations';
  carousel_position: number;
  carousel_class_years?: number[];
  carousel_subject?: string;
};

export const trackContentViewed = async (
  mindItem: MindItem,
  options?: {
    position?: number;
    searchId?: string;
    pageSource?: ContentViewedPageSource;
  },
): Promise<void> => {
  const { position, searchId, pageSource } = options ?? {};
  await waitForAmpliReady();

  let contentType: any;
  let publicVideo: string | undefined;
  let courseDetails: {
    courseTheme: Course['theme'];
    courseDuration: Course['duration'];
    courseType: Course['type'];
  } | undefined;

  if (mindItem?.fileType === 'video') {
    contentType = amplitudeContentTypes.video;
    publicVideo = 'No';
  } else if (mindItem?.fileType === 'course') {
    contentType = amplitudeContentTypes.course;
    courseDetails = {
      courseTheme: mindItem.course.theme,
      courseDuration: mindItem.course.duration,
      courseType: mindItem.course.type,
    };
  } else {
    contentType = mindItem.isReadOnly ? amplitudeContentTypes.readOnlyDocument : amplitudeContentTypes.document;
  }

  const contentViewedPayload: AmplitudeTrackPayload | null = getContentEventPayload({
    content: {
      ...mindItem,
      publicVideo,
      ...courseDetails
    },
    event_type: amplitudeEvents.ContentViewed,
    contentType,
    searchId,
    pageSource,
  });

  if (contentViewedPayload) {
    if (searchId) {
      contentViewedPayload.event_properties = {
        ...contentViewedPayload.event_properties,
        ...(position !== undefined && { position }),
      } as typeof contentViewedPayload.event_properties;
    }
    services.track.ampliServerSideEventTrack(false, contentViewedPayload);
  }
};
